import {
  IApiResponse,
  IEstablishmentResponse,
  IPostPatientEstablishmentRelation,
  IPostProfessionalEstablishmentRelation,
  IUpdateEstablishmentPermissions,
  IUserSession,
  IHospitalCredentialResponse,
  IHospitalCredentialKeyResponse
} from 'types'
import { request } from 'services/base'
import {
  HEALTHCARE_FILTERED,
  HEALTHCARE_LIST_OF_PROFESSIONAL,
  HEALTHCARE_LOGO,
  HEALTHCARE_PATIENT_RELATION,
  HEALTHCARE_PROFESSIONAL_RELATION,
  PATIENT_LIST_OF_HEALTHCARE,
  PROFESSIONAL_LIST_OF_HEALTHCARE,
  TOGGLE_HEALTHCARE_PERMISSION_BATCH,
  HOSPITAL_CREDENTIALS,
  ROTATE_HOSPITAL_CREDENTIALS
} from './CONSTANTS'
import { IPatientResponse } from 'types/patient/types'

export const getUserEstablishment = async (
  professionalId?: string
): Promise<IApiResponse<IEstablishmentResponse[]>> =>
  await request({
    url: HEALTHCARE_LIST_OF_PROFESSIONAL(professionalId),
    method: 'get'
  })

/**
 * Busca um estabelecimento pelo id.
 *
 * Usado pelo relatório de impressão, que precisa do estabelecimento dono do
 * exame (`id_hospital`) e não pode depender do estabelecimento do Redux — esse
 * estado é sobrescrito por um stub contendo apenas `establishment_id` a cada
 * carga de página. Ver `.specs/20260720_establishment_redux_stub_bug/`.
 */
export const getEstablishmentById = async (
  establishmentId: string
): Promise<IApiResponse<IEstablishmentResponse>> =>
  await request({
    url: HEALTHCARE_FILTERED(establishmentId),
    method: 'get',
    disableToast: true
  })

export const addUserInEstablishment = async (
  data?: IPostProfessionalEstablishmentRelation
): Promise<IApiResponse<any>> =>
  await request({ url: HEALTHCARE_PROFESSIONAL_RELATION, data, method: 'post' })

export const getProfessionalListInEstablishment = async (
  establishmentId?: string
): Promise<IApiResponse<IUserSession[]>> =>
  await request({
    url: PROFESSIONAL_LIST_OF_HEALTHCARE(establishmentId),
    method: 'get'
  })

export const getEstablishmentPatientList = async (
  patientId: string,
  listType: 'active' | 'success' | 'discarded' | null
): Promise<IApiResponse<IPatientResponse[]>> =>
  await request({
    url: PATIENT_LIST_OF_HEALTHCARE(patientId, listType),
    method: 'get'
  })

export const addPatientInEstablishment = async (
  data?: IPostPatientEstablishmentRelation
): Promise<IApiResponse<any>> =>
  await request({ url: HEALTHCARE_PATIENT_RELATION, data, method: 'post' })

export const addProfessionalInEstablishment = async (
  data?: IPostProfessionalEstablishmentRelation
): Promise<IApiResponse<any>> =>
  await request({ url: HEALTHCARE_PROFESSIONAL_RELATION, data, method: 'post' })

export const deleteEstablishmentPatientRelation = async (
  patientId: string,
  establishmentId: string
): Promise<IApiResponse<any>> =>
  await request({
    url: HEALTHCARE_PATIENT_RELATION,
    method: 'delete',
    data: {
      establishment_id: establishmentId,
      patients: [patientId]
    }
  })

export const deleteEstablishmentProfessionalRelation = async (
  professionalId: string,
  establishmentId: string
): Promise<IApiResponse<unknown>> =>
  await request({
    url: HEALTHCARE_PROFESSIONAL_RELATION,
    method: 'delete',
    disableToast: true,
    data: {
      establishment_id: establishmentId,
      professionals: [professionalId]
    }
  })

export const toggleBatchHealthcarePermissions = async (
  data: IUpdateEstablishmentPermissions[]
): Promise<IApiResponse<any>> =>
  await request({
    url: TOGGLE_HEALTHCARE_PERMISSION_BATCH,
    method: 'post',
    data
  })

export const patchEstablishmentLogo = async (
  establishmentId: string,
  data: FormData
): Promise<IApiResponse<{ url: string }>> =>
  await request({
    url: HEALTHCARE_LOGO(establishmentId),
    method: 'patch',
    data
  })

export const removeEstablishmentLogo = async (
  establishmentId: string
): Promise<IApiResponse<{ url: null }>> =>
  await request({
    url: HEALTHCARE_LOGO(establishmentId),
    method: 'patch',
    data: { logo_url: null }
  })

export const getHospitalCredentials = async (
  establishmentId: string
): Promise<IApiResponse<IHospitalCredentialResponse>> =>
  await request({
    url: HOSPITAL_CREDENTIALS(establishmentId),
    method: 'get',
    disableToast: true
  })

export const createHospitalCredentials = async (
  establishmentId: string
): Promise<IApiResponse<IHospitalCredentialKeyResponse>> =>
  await request({
    url: HOSPITAL_CREDENTIALS(establishmentId),
    method: 'post',
    disableToast: true
  })

export const rotateHospitalCredentials = async (
  establishmentId: string
): Promise<IApiResponse<IHospitalCredentialKeyResponse>> =>
  await request({
    url: ROTATE_HOSPITAL_CREDENTIALS(establishmentId),
    method: 'post',
    disableToast: true
  })

export const revokeHospitalCredentials = async (
  establishmentId: string
): Promise<IApiResponse<void>> =>
  await request({
    url: HOSPITAL_CREDENTIALS(establishmentId),
    method: 'delete',
    disableToast: true
  })
